import { FC, PropsWithChildren, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { UserState } from '../user/UserState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { RecordingState } from '../recording/RecordingState';
import { IntroScreenState } from './IntroScreenState';
import { FullScreenOverlayState } from '../support/FullScreenOverlayState';
import { useFadeTransition } from '../hooks/useFadeTransition';
import { IntroRecordButton } from './IntroRecordButton';
import { VideoBackground } from './VideoBackground';

const TRANSITION_MS = 900;

const useStyles = createUseStyles({
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 50000, // must be higher than ionic z-index on recording sheet modal
        display: 'flex',
        flexDirection: 'column'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1
    },
    hero: {
        position: 'relative',
        height: '65dvh',
        minHeight: 350,
        width: '100%',
        overflow: 'hidden',
        flexShrink: 0
    },
    gradientOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        background: `linear-gradient(180deg,
            rgba(0,0,0,0.05) 0%,
            rgba(0,0,0,0.1) 30%,
            rgba(255,255,255,0.4) 65%,
            rgba(255,255,255,1) 85%,
            rgba(255,255,255,1) 100%
        )`
    },
    skipLink: {
        position: 'absolute',
        top: 12,
        right: 16,
        marginTop: 'var(--ion-safe-area-top)',
        color: 'rgba(255,255,255,0.7)',
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        zIndex: 2,
        padding: '8px 4px'
    },
    heroBottom: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: 16
    },
    beaAvatar: {
        width: 96,
        height: 96,
        borderRadius: '50%',
        objectFit: 'cover',
        border: '2px solid rgba(255,255,255,0.6)'
    },
    greeting: {
        fontSize: 22,
        fontWeight: 500,
        color: '#032A37',
        marginTop: 12,
        textAlign: 'center'
    },
    cta: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 32px',
        textAlign: 'center',
        backgroundColor: '#fff'
    },
    heading: {
        fontSize: 18,
        fontWeight: 500,
        color: '#032A37',
        marginBottom: 12,
        lineHeight: 1.4
    },
    description: {
        fontSize: 14,
        color: '#687f87',
        lineHeight: 1.6,
        marginBottom: 32,
        maxWidth: 480
    },
});

export const IntroScreen: FC<PropsWithChildren> = ({ children }) => {
    const classes = useStyles();
    const userState = useInstance(UserState);
    useSubscription(() => userState);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const recordingState = useInstance(RecordingState);
    const introScreenState = useInstance(IntroScreenState);
    useSubscription(() => introScreenState);
    const overlayState = useInstance(FullScreenOverlayState);

    const firstName = userState.state.firstName || '';
    const visible = introScreenState.isVisible();

    const { isFadingOut, fadeRef } = useFadeTransition({
        visible,
        durationMs: TRANSITION_MS
    });

    useEffect(() => {
        if (visible) {
            mixpanel.track('Intro Screen Viewed');
        }
    }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

    // Treat the intro overlay as visible through its fade-out so events under
    // it don't fire mid-transition. Mirrors the render condition below.
    useEffect(() => {
        if (visible || isFadingOut) {
            overlayState.open('intro-screen');
            return () => overlayState.close('intro-screen');
        }
    }, [visible, isFadingOut, overlayState]);

    const handleRecord = () => {
        // No summaryPrompt: the recorder decides. This overlay is reachable by an existing user
        // whose profile is incomplete, and forcing 'intro' would re-run profile extraction over
        // a care recipient they already set up.
        recordingState.triggerRecording({ initiatedFrom: 'Intro Screen' });
        introScreenState.hide();
    };

    const handleSkip = () => {
        if (isFadingOut) {
            return;
        }
        mixpanel.track('Intro Skipped');
        introScreenState.hide();
    };

    // Not visible — render children only
    if (!visible && !isFadingOut) {
        return <>{children}</>;
    }

    // Showing intro (or fading out) — render children behind the overlay so the app is revealed during fade
    // Portal renders the overlay at document.body level so it sits above Ionic modals/sheets
    const overlay = (
        <div className={classes.overlay}>
            <div ref={fadeRef} className={classes.content}>
                <div className={classes.hero}>
                    <VideoBackground skipIncrement />
                    <div className={classes.gradientOverlay} />
                    <div className={classes.skipLink} onClick={handleSkip} data-id="intro-skip">
                        Skip
                    </div>
                    <div className={classes.heroBottom}>
                        <img
                            src="/assets/images/help/helper-bea-large.png"
                            alt="Bea"
                            className={classes.beaAvatar}
                        />
                        {firstName && (
                            <div className={classes.greeting}>
                                Nice to meet you, {firstName}.
                            </div>
                        )}
                    </div>
                </div>

                <div className={classes.cta}>
                    <div className={classes.heading}>
                        Let's set up a care profile.
                    </div>
                    <div className={classes.description}>
                        Who are you caring for? Share their <strong>name</strong>,{' '}
                        <strong>diagnoses</strong> they're managing, the{' '}
                        <strong>medications</strong> they take, and anything else you'd
                        like me to know about them. I'll create a <strong>care profile</strong>{' '}
                        for you so you don't have to hold it all in your head.
                    </div>
                    <IntroRecordButton onRecord={handleRecord} disabled={isFadingOut} transitionMs={TRANSITION_MS} />
                </div>
            </div>
        </div>
    );

    return (
        <>
            {isFadingOut && children}
            {createPortal(overlay, document.body)}
        </>
    );
};
